import { Injectable } from '@angular/core';
import * as XLSX from 'xlsx';

export enum ExportType {
  CSV = 'CSV',
  XLSX = 'XLSX'
}

@Injectable({
  providedIn: 'root'
})
export class ExportService {

  constructor() { }

  /**
   * Export data to CSV format and download
   * @param data Array of objects to export
   * @param filename Name of the file (without extension)
   * @param columns Optional array of column names to include, or array of {key, label} objects
   * @returns Promise that resolves when download is triggered
   */
  exportToCSV(data: any[], filename: string = 'export', columns?: string[] | {key: string, label: string}[]): Promise<void> {
    if (!data || data.length === 0) {
      console.warn('No data to export');
      return Promise.reject(new Error('No data to export'));
    }

    // Handle different column formats
    let headerLabels: string[];
    let dataKeys: string[];
    
    if (!columns) {
      // No columns specified, use all keys from first object
      dataKeys = Object.keys(data[0]);
      headerLabels = dataKeys;
    } else if (typeof columns[0] === 'string') {
      // Array of strings - use as both keys and labels
      dataKeys = columns as string[];
      headerLabels = columns as string[];
    } else {
      // Array of {key, label} objects
      const columnObjs = columns as {key: string, label: string}[];
      dataKeys = columnObjs.map(col => col.key);
      headerLabels = columnObjs.map(col => col.label);
    }
    
    // Create CSV content
    const csvContent = [
      // Header row
      headerLabels.join(','),
      // Data rows
      ...data.map(row => 
        dataKeys.map(key => {
          const value = row[key];
          // Handle values that contain commas, quotes, or newlines
          if (typeof value === 'string' && (value.includes(',') || value.includes('"') || value.includes('\n'))) {
            return `"${value.replace(/"/g, '""')}"`;
          }
          return value || '';
        }).join(',')
      )
    ].join('\n');

    // Create and download file
    return this.downloadFile(csvContent, `${filename}.csv`, 'text/csv;charset=utf-8;');
  }

  /**
   * Export data to XLSX format and download
   * @param data Array of objects to export
   * @param filename Name of the file (without extension)
   * @param sheetName Name of the worksheet
   * @param columns Optional array of column names to include, or array of {key, label} objects
   * @returns Promise that resolves when download is triggered
   */
  exportToXLSX(data: any[], filename: string = 'export', sheetName: string = 'Sheet1', columns?: string[] | {key: string, label: string}[]): Promise<void> {
    if (!data || data.length === 0) {
      console.warn('No data to export');
      return Promise.reject(new Error('No data to export'));
    }

    // Handle different column formats and prepare data
    let exportData = data;
    let headerLabels: string[];
    
    if (!columns) {
      // No columns specified, use all data as is
      headerLabels = Object.keys(data[0]);
    } else if (typeof columns[0] === 'string') {
      // Array of strings - filter data by these keys
      const stringColumns = columns as string[];
      exportData = data.map(row => {
        const filteredRow: any = {};
        stringColumns.forEach(col => {
          filteredRow[col] = row[col];
        });
        return filteredRow;
      });
      headerLabels = stringColumns;
    } else {
      // Array of {key, label} objects - filter data by keys but use labels for headers
      const columnObjs = columns as {key: string, label: string}[];
      const dataKeys = columnObjs.map(col => col.key);
      headerLabels = columnObjs.map(col => col.label);
      
      exportData = data.map(row => {
        const filteredRow: any = {};
        columnObjs.forEach(col => {
          filteredRow[col.label] = row[col.key]; // Use label as key in export data
        });
        return filteredRow;
      });
    }

    // Create workbook and worksheet
    const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet(exportData);
    const wb: XLSX.WorkBook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, sheetName);

    // Auto-size columns
    const range = XLSX.utils.decode_range(ws['!ref'] || 'A1');
    const colWidths: any[] = [];
    
    for (let C = range.s.c; C <= range.e.c; ++C) {
      let maxWidth = 10;
      for (let R = range.s.r; R <= range.e.r; ++R) {
        const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
        const cell = ws[cellAddress];
        if (cell && cell.v) {
          const cellLength = cell.v.toString().length;
          maxWidth = Math.max(maxWidth, cellLength);
        }
      }
      colWidths.push({ width: Math.min(maxWidth + 2, 50) });
    }
    ws['!cols'] = colWidths;

    // Save file
    return new Promise<void>((resolve, reject) => {
      try {
        XLSX.writeFile(wb, `${filename}.xlsx`);
        // Give browser time to trigger download
        setTimeout(() => resolve(), 100);
      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * Helper method to download file content
   * @param content File content
   * @param filename Filename with extension
   * @param mimeType MIME type of the file
   * @returns Promise that resolves when download is triggered
   */
  public downloadFile(content: string, filename: string, mimeType: string): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      try {
        const blob = new Blob([content], { type: mimeType });
        const url = window.URL.createObjectURL(blob);
        
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        link.style.display = 'none';
        
        document.body.appendChild(link);
        link.click();
        
        // Clean up after a short delay to allow browser to start download
        setTimeout(() => {
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
          resolve();
        }, 100);
      } catch (error) {
        reject(error);
      }
    });
  }
}
